import { z } from 'zod';
import { CurrencyCodeSchema } from '../common/enums.js';
import { MetadataSchema, PaginationQuerySchema } from '../common/types.js';
export const CouponTypeSchema = z.enum(['fixed_amount', 'percentage']);
export const DurationTypeSchema = z.enum(['forever', 'months']);
export const CouponStatusSchema = z.enum(['active', 'draft', 'archived']);
export const AppliedCouponStatusSchema = z.enum([
    'active',
    'expired',
    'cancelled',
]);
export const CouponSchema = z.object({
    id: z.string(),
    tenant_id: z.string(),
    environment_id: z.string().optional(),
    name: z.string(),
    code: z.string(),
    description: z.string().optional(),
    coupon_type: CouponTypeSchema,
    amount_cents: z.number().min(0).nullable().optional(),
    currency: CurrencyCodeSchema.nullable().optional(),
    percentage_rate: z.number().min(0).max(100).nullable().optional(),
    duration_type: DurationTypeSchema,
    duration_in_months: z.number().min(1).nullable().optional(),
    redeem_after: z.string().datetime().nullable().optional(),
    redeem_before: z.string().datetime().nullable().optional(),
    max_redemptions: z.number().min(1).nullable().optional(),
    times_redeemed: z.number().min(0).default(0),
    status: CouponStatusSchema,
    metadata: MetadataSchema,
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
});
export const CreateCouponSchema = z
    .object({
    name: z.string().min(1).max(255),
    code: z
        .string()
        .min(1)
        .max(100)
        .regex(/^[A-Z0-9_-]+$/, 'Code must contain only uppercase letters, numbers, underscores, and hyphens'),
    description: z.string().max(1000).optional(),
    coupon_type: CouponTypeSchema,
    amount_cents: z.number().min(0).nullable().optional(),
    currency: CurrencyCodeSchema.nullable().optional(),
    percentage_rate: z.number().min(0).max(100).nullable().optional(),
    duration_type: DurationTypeSchema,
    duration_in_months: z.number().min(1).max(120).optional(),
    redeem_after: z.string().datetime().optional(),
    redeem_before: z.string().datetime().optional(),
    max_redemptions: z.number().min(1).optional(),
    status: CouponStatusSchema.optional(),
    metadata: MetadataSchema.default({}),
})
    .refine(data => {
    if (data.coupon_type === 'fixed_amount') {
        return data.amount_cents !== undefined && data.amount_cents !== null;
    }
    if (data.coupon_type === 'percentage') {
        return (data.percentage_rate !== undefined && data.percentage_rate !== null);
    }
    return true;
}, {
    message: 'amount_cents required for fixed_amount coupons, percentage_rate required for percentage coupons',
})
    .refine(data => {
    if (data.duration_type === 'months') {
        return data.duration_in_months !== undefined;
    }
    return true;
}, {
    message: 'duration_in_months required when duration_type is months',
});
export const UpdateCouponSchema = z.object({
    name: z.string().min(1).max(255).optional(),
    description: z.string().max(1000).optional(),
    coupon_type: CouponTypeSchema.optional(),
    amount_cents: z.number().min(0).nullable().optional(),
    currency: CurrencyCodeSchema.nullable().optional(),
    percentage_rate: z.number().min(0).max(100).nullable().optional(),
    duration_type: DurationTypeSchema.optional(),
    duration_in_months: z.number().min(1).max(120).optional(),
    redeem_after: z.string().datetime().optional(),
    redeem_before: z.string().datetime().optional(),
    max_redemptions: z.number().min(1).optional(),
    status: CouponStatusSchema.optional(),
    metadata: MetadataSchema.optional(),
});
export const CouponQuerySchema = PaginationQuerySchema.extend({
    search: z.string().optional(),
    status: z.union([CouponStatusSchema, z.literal('all')]).optional(),
    type: z.union([CouponTypeSchema, z.literal('all')]).optional(),
    sort_by: z
        .enum(['name', 'code', 'created_at', 'updated_at', 'times_redeemed'])
        .optional(),
    sort_order: z.enum(['asc', 'desc']).optional(),
});
export const CouponParamsSchema = z.object({
    id: z.string(),
});
export const CouponResponseSchema = CouponSchema;
export const PaginatedCouponsResponseSchema = z.object({
    data: z.array(CouponResponseSchema),
    pagination: z.object({
        page: z.number(),
        limit: z.number(),
        total: z.number(),
        total_pages: z.number(),
        has_next: z.boolean(),
        has_prev: z.boolean(),
    }),
});
export const DuplicateCouponSchema = z.object({
    name: z.string().min(1).max(255).optional(),
    code: z
        .string()
        .min(1)
        .max(100)
        .regex(/^[A-Z0-9_-]+$/)
        .optional(),
    status: CouponStatusSchema.optional(),
});
export const CouponUsageStatsSchema = z.object({
    total_redemptions: z.number(),
    remaining_redemptions: z.number().optional(),
    usage_percentage: z.number().min(0).max(100),
    is_expired: z.boolean(),
    is_at_limit: z.boolean(),
    days_until_expiry: z.number().optional(),
});
export const AppliedCouponSchema = z.object({
    id: z.string(),
    tenant_id: z.string(),
    environment_id: z.string().optional(),
    coupon_id: z.string(),
    contract_id: z.string().optional(),
    customer_id: z.string().optional(),
    status: AppliedCouponStatusSchema,
    amount_cents: z.number().min(0).nullable().optional(),
    currency: CurrencyCodeSchema.nullable().optional(),
    percentage_rate: z.number().min(0).max(100).nullable().optional(),
    frequency: z.string(),
    frequency_duration: z.number().optional(),
    frequency_duration_remaining: z.number().optional(),
    applied_at: z.string().datetime(),
    expires_at: z.string().datetime().optional(),
    cancelled_at: z.string().datetime().optional(),
    metadata: MetadataSchema,
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
});
export const CreateAppliedCouponSchema = z.object({
    coupon_id: z.string(),
    contract_id: z.string().optional(),
    customer_id: z.string().optional(),
    metadata: MetadataSchema.optional(),
});
export const COUPON_TYPE = CouponTypeSchema.enum;
export const COUPON_TYPES = CouponTypeSchema.options;
export const DURATION_TYPE = DurationTypeSchema.enum;
export const DURATION_TYPES = DurationTypeSchema.options;
export const COUPON_STATUS = CouponStatusSchema.enum;
export const COUPON_STATUSES = CouponStatusSchema.options;
export const APPLIED_COUPON_STATUS = AppliedCouponStatusSchema.enum;
export const APPLIED_COUPON_STATUSES = AppliedCouponStatusSchema.options;
//# sourceMappingURL=index.js.map